// src/services/PeriodicReportingService.js
import auditService from './AuditService';
import moduleFreshnessService from './ModuleFreshnessService';
import storage from './StorageAdapter';
import entityService from './EntityService';
import { buildTemplatedExport } from '../modules/PeriodicReporting/utils/fiExport';
import { DEFAULT_TEMPLATE_ID } from '../modules/PeriodicReporting/config/reportTemplates';
import { getRequiredFields } from '../modules/PeriodicReporting/utils/schemaLoader';

const REPORT_STATUSES = ['draft', 'review', 'approved'];

// Valid status transitions: which statuses can be reached from each status
const VALID_TRANSITIONS = {
  draft: ['review'],
  review: ['draft', 'approved'],
  approved: [],
};

// Conditional section visibility rules.
// When condField equals condValue, the dynamic section identified by dynKey is visible.
// Otherwise the section is hidden from the form.
const CONDITIONAL_SECTIONS = [
  {
    id: 'A44_A45_VISIBLE_IF_A43_JA',
    dynKey: 'DYN-ROWS-A_01_02',
    condField: 'DP-G6N7-FOQX',  // A43: Finns det kunder i annan EU-stat via frihet att erbjuda tjänster?
    condValue: 'Ja',
  },
];

// Conditional field-level visibility rules.
// When condField equals condValue, the follow-up fields (followupIds) are visible.
// Otherwise those fields are hidden from the section form.
// This enables smart Ja/Nej branching within a section.
const CONDITIONAL_FIELDS = [
  {
    id: 'B1_JA_SHOWS_B2_B3_B13_B14',
    condField: 'DP-XPMM-DNFM',  // B1: Har företaget genomfört en allmän riskbedömning?
    condValue: 'Ja',
    followupIds: [
      'DP-KLLK-OCC4',  // B2: Senaste uppdatering av den allmänna riskbedömningen
      'DP-AGRX-QBMI',  // B3: Närmast föregående uppdatering av den allmänna riskbedömningen
      'DP-KJJL-G9PQ',  // B13: Länder bedömda som hög risk i den allmänna riskbedömningen
      'DP-K8SU-C2WV',  // B14: Beaktas högrisktredjeländer i den allmänna riskbedömningen?
    ],
  },
  {
    id: 'B1_NEJ_SHOWS_B4',
    condField: 'DP-XPMM-DNFM',  // B1: Har företaget genomfört en allmän riskbedömning?
    condValue: 'Nej',
    followupIds: [
      'DP-IOZA-UQ3W',  // B4: Om företaget inte genomfört en allmänriskbedömning, beskriv anledningen
    ],
  },
];

// Configurable cross-field validation rules.
// Each rule defines a relationship between data point fields.
// Rule types:
//   'lte'        — field value must be ≤ refField value (both must be numeric and present)
//   'requiredIf' — field is required when condField has a non-empty value
const CROSS_FIELD_RULES = [
  {
    id: 'A2_LTE_A1',
    type: 'lte',
    field: 'DP-BL0D-RPDJ',     // A2: beneficial owners with foreign tax domicile
    refField: 'DP-GINW-BFCP',  // A1: total beneficial owners
    messageKey: 'modules.periodicReporting.validation.crossFieldLte',
    messageParams: { fiId: 'A2', refFiId: 'A1' },
  },
  {
    id: 'B3_REQUIRED_IF_B2',
    type: 'requiredIf',
    field: 'DP-AGRX-QBMI',     // B3: previous update date of general risk assessment
    condField: 'DP-KLLK-OCC4', // B2: latest update date of general risk assessment
    messageKey: 'modules.periodicReporting.validation.crossFieldRequiredIf',
    messageParams: { fiId: 'B3', condFiId: 'B2' },
  },
];

const DEFAULT_SCHEDULE = {
  enabled: false,
  month: 1,
  day: 15,
  targetYearOffset: -1,
  assignees: '',
  lastAutoCreated: null,
};

class PeriodicReportingService {
  constructor() {
    this.REPORTS_KEY = 'periodicreporting_reports';
    this.SCHEDULE_KEY = 'periodicreporting_schedule';
    this.APPROVAL_WORKFLOW_KEY = 'periodicreporting_approval_workflow';
    this.DISTRIBUTION_LIST_KEY = 'periodicreporting_distribution_list';
    this.listeners = [];

    if (typeof window !== 'undefined') {
      window.addEventListener('rms_active_entity_changed', () => {
        this.notifyListeners();
      });
    }
  }

  _entityKey(baseKey) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${baseKey}_${entityId}` : baseKey;
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(l => l());
  }

  // === REPORTS ===

  getReports() {
    return storage.getJSON(this._entityKey(this.REPORTS_KEY), []);
  }

  getReport(id) {
    return this.getReports().find(r => r.id === id) || null;
  }

  getReportByYear(year) {
    return this.getReports().find(r => r.year === year) || null;
  }

  getReportYears() {
    const reports = this.getReports();
    const years = [...new Set(reports.map(r => r.year))];
    return years.sort((a, b) => b - a);
  }

  async createReport(year, createdBy = 'system') {
    const existing = this.getReportByYear(year);
    if (existing) {
      throw new Error(`Report for year ${year} already exists`);
    }

    // Inherit interpretation comments from the most recent previous report
    const allReports = this.getReports();
    const previousReport = allReports
      .filter(r => r.year < year)
      .sort((a, b) => b.year - a.year)[0];
    const inheritedInterpretationComments = previousReport?.interpretationComments || {};

    const report = {
      id: `prpt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      year,
      status: 'draft',
      schemaVersion: '4.0',
      fields: {},
      attachments: [],
      interpretationComments: inheritedInterpretationComments,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy,
    };
    allReports.push(report);
    await storage.setJSON(this._entityKey(this.REPORTS_KEY), allReports);
    await moduleFreshnessService.markModified('PeriodicReporting');
    auditService.log('CREATE', 'PeriodicReporting', { entity: 'Report', year });
    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.emit('reportCreated', { reportId: report.id, year, createdBy });
    } catch (_e) { /* best-effort */ }
    this.notifyListeners();
    return report;
  }

  async updateReport(id, updates) {
    const reports = this.getReports();
    const idx = reports.findIndex(r => r.id === id);
    if (idx < 0) return null;
    reports[idx] = {
      ...reports[idx],
      ...updates,
      id: reports[idx].id,
      year: reports[idx].year,
      createdAt: reports[idx].createdAt,
      createdBy: reports[idx].createdBy,
      updatedAt: new Date().toISOString(),
    };
    await storage.setJSON(this._entityKey(this.REPORTS_KEY), reports);
    await moduleFreshnessService.markModified('PeriodicReporting');
    this.notifyListeners();
    return reports[idx];
  }

  async updateReportStatus(id, status, updatedBy = 'system') {
    if (!REPORT_STATUSES.includes(status)) {
      throw new Error(`Invalid status: ${status}. Must be one of: ${REPORT_STATUSES.join(', ')}`);
    }
    const report = this.getReport(id);
    if (!report) return null;
    const historyEntry = {
      from: report.status,
      to: status,
      by: updatedBy,
      at: new Date().toISOString(),
      comment: '',
    };
    const statusHistory = [...(report.statusHistory || []), historyEntry];
    auditService.log('STATUS_TRANSITION', 'PeriodicReporting', {
      entity: 'Report',
      year: report.year,
      id,
      from: report.status,
      to: status,
      by: updatedBy,
    });
    return await this.updateReport(id, {
      status,
      statusUpdatedAt: historyEntry.at,
      statusUpdatedBy: updatedBy,
      statusHistory,
    });
  }

  // === APPROVAL WORKFLOW CONFIG ===

  getApprovalWorkflowConfig() {
    return storage.getJSON(this._entityKey(this.APPROVAL_WORKFLOW_KEY), { enabled: false, steps: [] });
  }

  async saveApprovalWorkflowConfig(config) {
    await storage.setJSON(this._entityKey(this.APPROVAL_WORKFLOW_KEY), config);
    this.notifyListeners();
  }

  // === DISTRIBUTION LIST ===

  getDistributionList() {
    return storage.getJSON(this._entityKey(this.DISTRIBUTION_LIST_KEY), []);
  }

  async saveDistributionList(list) {
    await storage.setJSON(this._entityKey(this.DISTRIBUTION_LIST_KEY), list);
    this.notifyListeners();
  }

  /**
   * Records distribution delivery on the report and notifies system users.
   * Called after final approval (both single-step and multi-step).
   * @private
   */
  async _triggerDistribution(report, approvedBy) {
    const list = this.getDistributionList();
    const at = new Date().toISOString();
    const distributionLog = {
      sentAt: at,
      sentBy: approvedBy,
      recipients: list.map(r => ({ ...r, status: 'sent', at })),
    };

    await this.updateReport(report.id, { distributionLog });
    auditService.log('DISTRIBUTION', 'PeriodicReporting', {
      entity: 'Report',
      year: report.year,
      id: report.id,
      recipientCount: list.length,
      by: approvedBy,
    });

    // Emit webhook for approval granted
    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.emit('approvalGranted', { type: 'report', reportId: report.id, year: report.year, approvedBy });
    } catch (_e) { /* best-effort */ }

    if (list.length > 0) {
      // Notify system users via NotificationService (lazy require to avoid circular dep)
      try {
        // eslint-disable-next-line
        const ns = require('./NotificationService').default;
        ns.addAssigneeNotification({
          id: `report_distributed_${report.id}`,
          year: report.year,
          route: '/periodic-reporting',
          assignees: '',
          titleKey: 'notifications.reportDistributed',
          titleParams: { year: report.year },
          descKey: 'notifications.reportDistributedDesc',
          descParams: { year: report.year, count: list.length },
        });
      } catch (e) { /* silently ignore — notification is best-effort */ }
    }
  }

  // Returns true when the report has an active multi-step workflow
  isMultiStepMode(report) {
    return !!(report && Array.isArray(report.approvalSteps) && report.approvalSteps.length > 0);
  }

  // === SINGLE-STEP AND MULTI-STEP APPROVAL ===

  // Submit a draft report for review
  async submitForReview(id) {
    const currentUser = auditService.getCurrentUser();
    const report = this.getReport(id);
    if (!report) throw new Error('Report not found');
    if (report.status !== 'draft') throw new Error('Only draft reports can be submitted for review');

    const config = this.getApprovalWorkflowConfig();
    if (config.enabled && Array.isArray(config.steps) && config.steps.length > 0) {
      // Multi-step: initialise workflow state on the report
      const approvalSteps = config.steps.map(step => ({
        id: step.id,
        label: step.label,
        status: 'pending',
        approvedBy: null,
        approvedAt: null,
        comment: '',
      }));
      const at = new Date().toISOString();
      const historyEntry = { from: 'draft', to: 'review', by: currentUser.username, at, comment: '' };
      auditService.log('STATUS_TRANSITION', 'PeriodicReporting', {
        entity: 'Report', year: report.year, id, from: 'draft', to: 'review', by: currentUser.username,
      });
      return await this.updateReport(id, {
        status: 'review',
        approvalSteps,
        approvalCurrentStep: 0,
        statusUpdatedAt: at,
        statusUpdatedBy: currentUser.username,
        statusHistory: [...(report.statusHistory || []), historyEntry],
      });
    }

    return await this.updateReportStatus(id, 'review', currentUser.username);
  }

  // Approve a report under review — for single-step mode only
  async approveReport(id) {
    const currentUser = auditService.getCurrentUser();
    const report = this.getReport(id);
    if (!report) throw new Error('Report not found');
    if (report.status !== 'review') throw new Error('Only reports under review can be approved');
    if (this.isMultiStepMode(report)) {
      throw new Error('Use approveCurrentStep() for multi-step approval workflows');
    }
    const updated = await this.updateReportStatus(id, 'approved', currentUser.username);
    if (updated) await this._triggerDistribution(updated, currentUser.username);
    return updated;
  }

  // Approve the current step in a multi-step workflow
  async approveCurrentStep(id, comment = '') {
    const currentUser = auditService.getCurrentUser();
    const report = this.getReport(id);
    if (!report) throw new Error('Report not found');
    if (report.status !== 'review') throw new Error('Report is not under review');
    if (!this.isMultiStepMode(report)) throw new Error('Report does not have a multi-step workflow');

    const steps = report.approvalSteps.map(s => ({ ...s }));
    const currentIdx = report.approvalCurrentStep ?? 0;
    if (currentIdx >= steps.length) throw new Error('All steps already completed');

    steps[currentIdx] = {
      ...steps[currentIdx],
      status: 'approved',
      approvedBy: currentUser.username,
      approvedAt: new Date().toISOString(),
      comment,
    };

    const nextIdx = currentIdx + 1;
    const allDone = nextIdx >= steps.length;

    if (allDone) {
      const at = new Date().toISOString();
      const historyEntry = {
        from: 'review',
        to: 'approved',
        by: currentUser.username,
        at,
        comment: '',
        detailKey: 'modules.periodicReporting.annual.form.allApprovalStepsCompleted',
        detailData: { count: steps.length },
      };
      auditService.log('STATUS_TRANSITION', 'PeriodicReporting', {
        entity: 'Report', year: report.year, id, from: 'review', to: 'approved', by: currentUser.username,
      });
      const updated = await this.updateReport(id, {
        status: 'approved',
        approvalSteps: steps,
        approvalCurrentStep: nextIdx,
        statusUpdatedAt: at,
        statusUpdatedBy: currentUser.username,
        statusHistory: [...(report.statusHistory || []), historyEntry],
      });
      if (updated) await this._triggerDistribution(updated, currentUser.username);
      return updated;
    }

    auditService.log('APPROVAL_STEP', 'PeriodicReporting', {
      entity: 'Report', year: report.year, id, stepIdx: currentIdx, by: currentUser.username,
    });
    this.notifyListeners();
    return await this.updateReport(id, {
      approvalSteps: steps,
      approvalCurrentStep: nextIdx,
    });
  }

  // Reject a report under review — returns it to draft with a comment
  async rejectReport(id, comment = '') {
    const currentUser = auditService.getCurrentUser();
    const report = this.getReport(id);
    if (!report) throw new Error('Report not found');
    if (report.status !== 'review') throw new Error('Only reports under review can be rejected');
    const historyEntry = {
      from: 'review',
      to: 'draft',
      by: currentUser.username,
      at: new Date().toISOString(),
      comment,
    };
    const statusHistory = [...(report.statusHistory || []), historyEntry];
    auditService.log('STATUS_TRANSITION', 'PeriodicReporting', {
      entity: 'Report',
      year: report.year,
      id,
      from: 'review',
      to: 'draft',
      by: currentUser.username,
      comment,
    });
    return await this.updateReport(id, {
      status: 'draft',
      approvalSteps: null,
      approvalCurrentStep: null,
      statusUpdatedAt: historyEntry.at,
      statusUpdatedBy: currentUser.username,
      statusHistory,
    });
  }

  canTransitionTo(report, targetStatus) {
    return (VALID_TRANSITIONS[report.status] || []).includes(targetStatus);
  }

  async updateReportFields(id, fields) {
    const report = this.getReport(id);
    if (!report) return null;
    const mergedFields = { ...report.fields, ...fields };
    return await this.updateReport(id, { fields: mergedFields });
  }

  // Normalize legacy attachment objects (pre-COMP-030) to the versioned format.
  _normalizeAttachments(attachments) {
    return (attachments || []).map(att => {
      if (att.versions) return att; // already versioned format
      // Legacy format: { name, size, type, addedAt }
      return {
        id: `att_legacy_${Math.random().toString(36).slice(2, 9)}`,
        name: att.name,
        versions: [{
          id: `attv_legacy_${Math.random().toString(36).slice(2, 9)}`,
          name: att.name,
          size: att.size,
          type: att.type || '',
          uploadedBy: att.uploadedBy || 'system',
          uploadedAt: att.addedAt || new Date().toISOString(),
        }],
      };
    });
  }

  async addAttachment(id, fileMetadata) {
    const report = this.getReport(id);
    if (!report) return null;
    const currentUser = auditService.getCurrentUser();
    const versionId = `attv_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const attachmentId = `att_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const version = {
      id: versionId,
      name: fileMetadata.name,
      size: fileMetadata.size,
      type: fileMetadata.type || '',
      uploadedBy: currentUser.username,
      uploadedAt: new Date().toISOString(),
    };
    const attachment = {
      id: attachmentId,
      name: fileMetadata.name,
      versions: [version],
    };
    const attachments = [...this._normalizeAttachments(report.attachments), attachment];
    auditService.log('ATTACHMENT_ADD', 'PeriodicReporting', { entity: 'Attachment', reportId: id, name: fileMetadata.name });
    return await this.updateReport(id, { attachments });
  }

  async addAttachmentVersion(reportId, attachmentId, fileMetadata) {
    const report = this.getReport(reportId);
    if (!report) return null;
    const currentUser = auditService.getCurrentUser();
    const attachments = this._normalizeAttachments(report.attachments);
    const idx = attachments.findIndex(a => a.id === attachmentId);
    if (idx < 0) return null;
    const version = {
      id: `attv_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: fileMetadata.name,
      size: fileMetadata.size,
      type: fileMetadata.type || '',
      uploadedBy: currentUser.username,
      uploadedAt: new Date().toISOString(),
    };
    attachments[idx] = {
      ...attachments[idx],
      name: fileMetadata.name,
      versions: [...attachments[idx].versions, version],
    };
    auditService.log('ATTACHMENT_VERSION', 'PeriodicReporting', { entity: 'Attachment', reportId, attachmentId, name: fileMetadata.name });
    return await this.updateReport(reportId, { attachments });
  }

  async deleteAttachment(reportId, attachmentId) {
    const report = this.getReport(reportId);
    if (!report) return null;
    const attachments = this._normalizeAttachments(report.attachments).filter(a => a.id !== attachmentId);
    auditService.log('ATTACHMENT_DELETE', 'PeriodicReporting', { entity: 'Attachment', reportId, attachmentId });
    return await this.updateReport(reportId, { attachments });
  }

  async deleteReport(id) {
    const reports = this.getReports().filter(r => r.id !== id);
    await storage.setJSON(this._entityKey(this.REPORTS_KEY), reports);
    auditService.log('DELETE', 'PeriodicReporting', { entity: 'Report', id });
    this.notifyListeners();
  }

  // === FIELD COMMENTS ===

  getFieldComments(reportId, fieldId) {
    const report = this.getReport(reportId);
    if (!report) return [];
    return ((report.comments || {})[fieldId] || []);
  }

  async addFieldComment(reportId, fieldId, text) {
    const report = this.getReport(reportId);
    if (!report) return null;
    const currentUser = auditService.getCurrentUser();
    const comment = {
      id: `cmt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      text,
      author: currentUser.username,
      timestamp: new Date().toISOString(),
    };
    const comments = { ...(report.comments || {}) };
    comments[fieldId] = [...(comments[fieldId] || []), comment];
    auditService.log('COMMENT', 'PeriodicReporting', { entity: 'ReportComment', reportId, fieldId });
    return await this.updateReport(reportId, { comments });
  }

  async deleteFieldComment(reportId, fieldId, commentId) {
    const report = this.getReport(reportId);
    if (!report) return null;
    const comments = { ...(report.comments || {}) };
    comments[fieldId] = (comments[fieldId] || []).filter(c => c.id !== commentId);
    return await this.updateReport(reportId, { comments });
  }

  // === INTERPRETATION COMMENTS ===

  getInterpretationComments(reportId) {
    const report = this.getReport(reportId);
    if (!report) return {};
    return report.interpretationComments || {};
  }

  async updateInterpretationComments(reportId, interpretationComments) {
    return await this.updateReport(reportId, { interpretationComments });
  }

  // === CROSS-FIELD VALIDATION ===

  /**
   * Returns the configured cross-field validation rules.
   * Rules can be extended by modifying CROSS_FIELD_RULES above.
   */
  getCrossFieldRules() {
    return CROSS_FIELD_RULES;
  }

  /**
   * Returns conditional section visibility rules.
   * Each rule specifies a dynamic section (dynKey) that is only shown
   * when a given field (condField) has the required value (condValue).
   */
  getConditionalSections() {
    return CONDITIONAL_SECTIONS;
  }

  /**
   * Returns conditional field-level visibility rules.
   * Each rule specifies a set of follow-up fields (followupIds) that are only shown
   * when a given field (condField) equals the required value (condValue).
   * When condField has a different value (or no value), the followupIds are hidden.
   */
  getConditionalFields() {
    return CONDITIONAL_FIELDS;
  }

  /**
   * Validates cross-field rules against the provided fields object.
   * Returns a plain object mapping fieldId → { ruleId, messageKey, params }
   * for each rule that is violated. Empty object means no violations.
   *
   * @param {Object} fields - report fields map
   * @returns {Object} violations map
   */
  validateCrossFieldRules(fields) {
    const violations = {};
    for (const rule of CROSS_FIELD_RULES) {
      const fieldVal = fields[rule.field];
      const hasFieldVal = fieldVal !== undefined && fieldVal !== '' && fieldVal !== null;

      if (rule.type === 'lte') {
        const refVal = fields[rule.refField];
        const hasRefVal = refVal !== undefined && refVal !== '' && refVal !== null;
        if (hasFieldVal && hasRefVal) {
          const num = Number(fieldVal);
          const refNum = Number(refVal);
          if (!isNaN(num) && !isNaN(refNum) && num > refNum) {
            violations[rule.field] = {
              ruleId: rule.id,
              messageKey: rule.messageKey,
              params: { ...rule.messageParams, value: num, max: refNum },
            };
          }
        }
      } else if (rule.type === 'requiredIf') {
        const condVal = fields[rule.condField];
        const hasCondVal = condVal !== undefined && condVal !== '' && condVal !== null;
        if (hasCondVal && !hasFieldVal) {
          violations[rule.field] = {
            ruleId: rule.id,
            messageKey: rule.messageKey,
            params: rule.messageParams,
          };
        }
      }
    }
    return violations;
  }

  // === SCHEDULE ===

  getSchedule() {
    return { ...DEFAULT_SCHEDULE, ...storage.getJSON(this._entityKey(this.SCHEDULE_KEY), {}) };
  }

  async saveSchedule(schedule) {
    await storage.setJSON(this._entityKey(this.SCHEDULE_KEY), schedule);
    this.notifyListeners();
  }

  /**
   * Checks whether a scheduled report should be auto-created for the current year.
   * Must be called on module load (check-on-load pattern).
   * Returns { report, year } if a new draft was created, or null otherwise.
   */
  async checkAndAutoCreateScheduledReports() {
    const schedule = this.getSchedule();
    if (!schedule.enabled) return null;

    const now = new Date();
    const currentYear = now.getFullYear();
    const month = Number(schedule.month) || 1;
    const day = Number(schedule.day) || 15;
    const scheduledDate = new Date(currentYear, month - 1, day);

    if (now < scheduledDate) return null;

    const reportYear = currentYear + (schedule.targetYearOffset ?? -1);
    const existing = this.getReportByYear(reportYear);
    if (existing) return null;

    const report = await this.createReport(reportYear, 'system (scheduled)');
    const updatedSchedule = {
      ...schedule,
      lastAutoCreated: { year: reportYear, date: new Date().toISOString() },
    };
    await storage.setJSON(this._entityKey(this.SCHEDULE_KEY), updatedSchedule);
    auditService.log('CREATE', 'PeriodicReporting', {
      entity: 'ScheduledReport',
      year: reportYear,
      trigger: 'schedule',
    });

    // Notify assignees via NotificationService.
    // Lazy require breaks the circular dependency
    // (NotificationService → PeriodicReportingService).
    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.addAssigneeNotification({
        id: `scheduled_report_created_${reportYear}`,
        year: reportYear,
        route: '/periodic-reporting',
        assignees: schedule.assignees || '',
      });
    } catch (e) { /* silently ignore — notification is best-effort */ }

    return { report, year: reportYear };
  }

  getStats() {
    const reports = this.getReports();
    return {
      draft: reports.filter(r => r.status === 'draft').length,
      review: reports.filter(r => r.status === 'review').length,
      approved: reports.filter(r => r.status === 'approved').length,
      total: reports.length,
    };
  }

  /**
   * Calculate how many required fields are filled in a report.
   * @param {Object} report - Report object (must have .fields)
   * @returns {{ filled: number, total: number }}
   */
  getReportCompletion(report) {
    const requiredFields = getRequiredFields();
    const fields = report.fields || {};
    let filled = 0;
    for (const id of requiredFields) {
      const val = fields[id];
      if (val === undefined || val === null || val === '') continue;
      if (Array.isArray(val) && val.length === 0) continue;
      filled++;
    }
    return { filled, total: requiredFields.size };
  }

  /**
   * Build an export payload using the template-driven architecture.
   * The format is defined by the template in config/reportTemplates.js —
   * changing export format requires only updating the template config.
   *
   * @param {Object} report - Report object
   * @param {string} [templateId] - Template ID (default: fi-4.0)
   * @returns {Object} Export payload
   */
  buildExportPayload(report, templateId = DEFAULT_TEMPLATE_ID) {
    return buildTemplatedExport(report, templateId);
  }

  /**
   * Export a single report as a templated payload.
   * @param {string} id - Report ID
   * @param {string} [templateId] - Export template (default: fi-4.0)
   * @returns {Object|null}
   */
  exportReport(id, templateId = DEFAULT_TEMPLATE_ID) {
    const report = this.getReport(id);
    if (!report) return null;
    const payload = this.buildExportPayload(report, templateId);
    auditService.log('EXPORT', 'PeriodicReporting', {
      entity: 'Report',
      year: report.year,
      id,
      templateId,
    });
    return payload;
  }

  /**
   * Export all approved reports as templated payloads.
   * @param {string} [templateId] - Export template (default: fi-4.0)
   * @returns {Object[]}
   */
  exportApprovedReports(templateId = DEFAULT_TEMPLATE_ID) {
    const reports = this.getReports().filter(r => r.status === 'approved');
    const payloads = reports.map(r => this.buildExportPayload(r, templateId));
    if (reports.length > 0) {
      auditService.log('EXPORT', 'PeriodicReporting', {
        entity: 'Reports',
        count: reports.length,
        years: reports.map(r => r.year),
        templateId,
      });
    }
    return payloads;
  }
}

const periodicReportingService = new PeriodicReportingService();
export default periodicReportingService;
